import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeTemplateDetail, AeTemplateSection } from '@/types/assessmentEngine';
import {
  AE_DEMOGRAPHIC_FIELD_KEYS,
  DEMOGRAPHICS_SECTION_CODE,
  type AeDemographicFieldKey,
  type AeTemplateDemographicsConfig,
} from './templateConfigTypes';

const DEMO_LABEL_KEYS: Record<AeDemographicFieldKey, string> = {
  name: 'assessmentEngine.demographics.name',
  email: 'assessmentEngine.demographics.email',
  phone: 'assessmentEngine.demographics.phone',
  address: 'assessmentEngine.demographics.address',
  title: 'assessmentEngine.demographics.title',
  company_name: 'assessmentEngine.demographics.companyName',
  position: 'assessmentEngine.demographics.position',
};

function findDemographicsSection(sections: AeTemplateSection[]): AeTemplateSection | undefined {
  return sections.find((s) => s.code === DEMOGRAPHICS_SECTION_CODE);
}

function contentSections(sections: AeTemplateSection[]): AeTemplateSection[] {
  return sections.filter((s) => s.code !== DEMOGRAPHICS_SECTION_CODE);
}

/**
 * Sync demographic questions section from template default_config.demographics.
 * Creates/updates SYS_DEMOGRAPHICS section at beginning or end of template.
 */
export async function syncDemographicSection(
  templateId: string,
  template: AeTemplateDetail,
  demographics: AeTemplateDemographicsConfig,
  t: (key: string) => string
): Promise<void> {
  const enabledFields = AE_DEMOGRAPHIC_FIELD_KEYS.filter((k) => demographics.fields[k]?.enabled);
  const existing = findDemographicsSection(template.sections);

  if (enabledFields.length === 0) {
    if (existing) {
      await assessmentEngineService.deleteSection(templateId, existing.id);
    }
    return;
  }

  let sectionId = existing?.id;
  if (!sectionId) {
    const created = await assessmentEngineService.createSection(templateId, {
      code: DEMOGRAPHICS_SECTION_CODE,
      name: t('assessmentEngine.builder.demographicsSectionName'),
      sort_order: demographics.placement === 'beginning' ? -1 : 9999,
      overview: t('assessmentEngine.builder.demographicsSectionOverview'),
    });
    sectionId = created.id;
  } else {
    await assessmentEngineService.updateSection(templateId, sectionId, {
      name: t('assessmentEngine.builder.demographicsSectionName'),
      overview: t('assessmentEngine.builder.demographicsSectionOverview'),
      sort_order: demographics.placement === 'beginning' ? -1 : 9999,
    });
  }

  const reloaded = await assessmentEngineService.getTemplate(templateId);
  const demoSection = findDemographicsSection(reloaded.sections);
  if (!demoSection) return;

  const existingByField = new Map<AeDemographicFieldKey, string>();
  for (const q of demoSection.questions) {
    const field = (q.validation_config as Record<string, unknown> | null)?.demographic_field as
      | AeDemographicFieldKey
      | undefined;
    if (field) existingByField.set(field, q.id);
  }

  for (let i = 0; i < enabledFields.length; i++) {
    const field = enabledFields[i];
    const cfg = demographics.fields[field];
    const labelKey = DEMO_LABEL_KEYS[field];
    const questionText = t(labelKey);
    const existingId = existingByField.get(field);

    if (existingId) {
      await assessmentEngineService.updateQuestion(templateId, existingId, {
        question_text: questionText,
        is_required: cfg.required,
        sort_order: i,
        validation_config: { demographic_field: field },
      });
    } else {
      await assessmentEngineService.createQuestion(templateId, {
        section_id: demoSection.id,
        question_key: `DEMO_${field.toUpperCase()}`,
        question_text: questionText,
        question_type: 'demographic',
        sort_order: i,
        is_required: cfg.required,
        validation_config: { demographic_field: field },
      });
    }
  }

  for (const [field, qId] of existingByField) {
    if (!enabledFields.includes(field)) {
      await assessmentEngineService.deleteQuestion(templateId, qId);
    }
  }

  void contentSections;
}
